import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useForm, Controller} from 'react-hook-form';
import Select from "react-select";
import { Tooltip } from 'react-tooltip';
import { format, differenceInDays } from 'date-fns';
import { it } from 'date-fns/locale';
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import debounce from "lodash.debounce";
import schedulePayService from '../../services/SchedulePayService';
import Nav from '../../components/schedulepay/NavSchedulePay';
import LayerEdit from '../../components/schedulepay/LayerEditCredit';
import LayerPays from '../../components/schedulepay/LayerPays';
import "../../static/css/datatable.css";

const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
    }).format(value);
};
const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 3 }, (_, i) => {
  const year = currentYear - i;
  return { label: year.toString(), value: year };
});
const currentMonth = new Date().getMonth() + 1;
const monthOptions = [
    { value: 1, label: "Gennaio" },
    { value: 2, label: "Febbraio" },
    { value: 3, label: "Marzo" },
    { value: 4, label: "Aprile" },
    { value: 5, label: "Maggio" },
    { value: 6, label: "Giugno" },
    { value: 7, label: "Luglio" },
    { value: 8, label: "Agosto" },
    { value: 9, label: "Settembre" },
    { value: 10, label: "Ottobre" },
    { value: 11, label: "Novembre" },
    { value: 12, label: "Dicembre" }
];

export default function CreditNote({ itemsPerPage = 25 }) {
    const defaultYearOptions = [
        { label: currentYear.toString(), value: currentYear },
       // { label: (currentYear - 1).toString(), value: currentYear - 1 }
    ];
    const defaultValues = {
        years: defaultYearOptions,
        months: [],
        status: [],
        idClient: "",
        idEvent:""
    };
    const resetAllValues = {
        years: [],
        months: [],
        status: [],
        idClient: "",
        idEvent:""
    };
    const { register, control, watch, handleSubmit, reset, getValues } = useForm({
        defaultValues
    });
    const [loading, setLoading] = useState(true);
    const [results, setResults] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [totalResults, setTotalResults] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPageLocal, setItemsPerPageLocal] = useState(itemsPerPage);
    const [pay, setPay] = useState({});
    const [loadLayerEdit, setLoadLayerEdit] = useState(false);
    const [isLayerOpen, setIsLayerOpen] = useState(false);
    const [openAction, setOpenAction] = useState(null);
    const [loadPaysLayer, setLoadPaysLayer] = useState(false);
    const [isPaysLayerOpen, setIsPaysLayerOpen] = useState(false);
    const [filter, setFilter] = useState(null);
    const [type, setType] = useState(null);

    const searchValue = watch("search", "");
    const searchFields = ["fullName", "nameEvent","amount"];
    const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
    const [openFilter, setOpenFilter] = useState(null);

    const totalPages = Math.ceil(filteredData.length / itemsPerPageLocal);
    const startIndex = (currentPage - 1) * itemsPerPageLocal;
    const endIndex = startIndex + itemsPerPageLocal;
    const currentItems = filteredData.slice(startIndex, endIndex);
    // Funzione di navigazione
    const goToPage = (page) => {
        setCurrentPage(page);
    };

    const normalizePayload = (formData) => {
        return {
            years: formData.years?.map(m => m.value) || [],
            months: formData.months?.map(m => m.value) || [],
            status: formData.status?.map(s => s.value) || [],
            mode: formData.mode?.value || null,
            idClient: formData.idClient ? Number(formData.idClient) : null,
            payType:"CREDIT_NOTE"
        };
    };

    const onReset = () => {
        reset(resetAllValues);
        fetchCreditNote(getValues());
    };
    const onDefault = () => {
        reset(defaultValues);
        fetchCreditNote(getValues());
    };
    const handleFilterClick = (index) => {
        setOpenFilter(openFilter === index ? null : index);
    };
    const fetchPaysFilter = (data) => {
        fetchCreditNote(data);
        setOpenFilter(false);
    };
    const onFetchPaysAfterEdit = () => {
        fetchCreditNote(getValues());
    };

    const fetchCreditNote= async (data) => {
        try {
            const payload = normalizePayload(data);
            const res = await schedulePayService.getFilterCredit(payload);
            // Estrarre i dati specifici dalla struttura complessa di response.data
            const resultsData = res.data || [];
            const total = res.data.length || 0;

            setResults(resultsData); // Salva i risultati
            setFilteredData(resultsData);
            setTotalResults(total); // Salva il totale dei risultati
            setLoading(false);
            // Calcoliamo dinamicamente itemsPerPageLocal dopo aver ricevuto i dati
            const defaultItemsPerPage = Math.min(itemsPerPage, total);
            setItemsPerPageLocal(defaultItemsPerPage);

        } catch (error) {
            console.error('Error fetching pays', error);
        }
    };

    useEffect(() => {
        fetchCreditNote(getValues());
    }, []);

    const filterData = useCallback((term) => {
        // se il termine è vuoto, lista originale
        if (!term || term.trim() === "") {
            const sorted = applySort(results, sortConfig);
            setFilteredData(sorted);
            return;
        }

        const lower = term.toLowerCase();

        const filtered = results.filter(item =>
            searchFields.some(field => {
                let value = item[field];

                if (!value) return false;

                // campo number formattato
                if (field === "number") {
                    value = value.toString().padStart(3, "0");
                }

                return value.toString().toLowerCase().includes(lower);
            })
        );

        // applica ordinamento anche ai risultati filtrati
        const sorted = applySort(filtered, sortConfig);

        setFilteredData(sorted);
        setCurrentPage(1);
    }, [results, sortConfig, searchFields]);

    const applySort = (data, sortCfg) => {
        const { key, direction } = sortCfg;

        if (!key || !direction) return data;

        return [...data].sort((a, b) => {
            let valA = a[key];
            let valB = b[key];

            if (key === "invoice" || key === "taxable") {
                valA = isNaN(valA) ? -Infinity : Number(valA);
                valB = isNaN(valB) ? -Infinity : Number(valB);
            } else {
                valA = valA?.toString().toLowerCase() || "";
                valB = valB?.toString().toLowerCase() || "";
            }

            if (valA < valB) return direction === "asc" ? -1 : 1;
            if (valA > valB) return direction === "asc" ? 1 : -1;
            return 0;
        });
    };

    const sortData = (key) => {
        let direction = "asc";
        if (sortConfig.key === key) {
            if (sortConfig.direction === "asc") direction = "desc";
            else if (sortConfig.direction === "desc") direction = null; // reset
        }
        const newSort = { key, direction };
        setSortConfig(newSort);

        // reset → torna tutti i dati filtrati senza sorting
        if (!direction) {
            filterData(searchValue);   // lascia filtro attivo ma senza ordinamento
            return;
        }
        const sorted = applySort(filteredData, newSort);
        setFilteredData(sorted);
    };

    const debouncedFilter = useMemo(
        () => debounce((term) => filterData(term), 300),
        [filterData]     // dipende SOLO da filterData
    );

    useEffect(() => {
        debouncedFilter(searchValue);
        return () => debouncedFilter.cancel();
    }, [searchValue, debouncedFilter]);

    useEffect(() => { //Quando results cambia per filtri applicati ricalcola la lista corretta:
        const sorted = applySort(results, sortConfig);
        setFilteredData(sorted);
    }, [results]);

    useEffect(() => { //Effetto che aggiorna i totali
        const total = filteredData.length;
        setTotalResults(total);

        if (total > 0) {
            setItemsPerPageLocal(Math.min(itemsPerPage, total));
        }
    }, [filteredData]);
    // Funzione per aprire/chiudere il pannello laterale
    const toggleLayerEdit = (pay) => {
        setLoadLayerEdit(true);
        setPay(pay);
        setIsLayerOpen(!isLayerOpen);
    };
    const togglePaysLayer = (filter,type) => {
        setLoadPaysLayer(true);
        setFilter(filter);
        setType(type);
        setIsPaysLayerOpen(true);
    };
    const toggleClosePaysLayer = () => {
        setLoadPaysLayer(false);
        setFilter(null);
        setType(null);
        setIsPaysLayerOpen(false);
    };
    // Funzione per aggiornare il numero di elementi per pagina
    const handleItemsPerPageChange = (event) => {
        setItemsPerPageLocal(parseInt(event.target.value, 10));
        setCurrentPage(1);
    };
    // Funzione per generare le opzioni dinamiche per la select
    const generateItemsPerPageOptions = () => {
        const options = [];
        const maxItems = totalResults;

        if (maxItems <= 2) {
          options.push(2);
        } else if (20 <= maxItems && maxItems <= 50){
          options.push(10, 25, maxItems);
        } else if (maxItems > 50) {
            options.push(10, 25, 50);
        } else {
            // Aggiungiamo anche multipli di 2 tra il minimo e il massimo
            for (let i = 2; i <= maxItems; i *= 2) {
                if (i < maxItems) options.push(i);
            }
            // Se il numero totale è abbastanza grande, aggiungiamo anche il numero massimo
            if (maxItems > 2) options.push(maxItems);
        }

        return options;
    };
    const getFormattedDate = () => {
      const date = new Date();
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${day}-${month}-${year}`;
    };
    const handleExportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Pagamenti");

        worksheet.columns = [
            { header: "Cliente", key: "client", width: 28 },
            { header: "Fattura", key: "invoice", width: 15 },
            { header: "Data Fattura", key: "invoiceDate", width: 15 },
            { header: "Pagamento (€)", key: "amount", width: 16 },
            { header: "Esito", key: "status", width: 12 },
            { header: "Note", key: "note", width: 30 },
            { header: "Evento", key: "event", width: 15 },
            { header: "Contratto", key: "contract", width: 15 },
            { header: "Imponibile Contratto", key: "amountContract", width: 15 },
            { header: "Imponibile rata", key: "taxable", width: 15 },
            { header: "Iva", key: "iva", width: 15 }
        ];

        worksheet.getRow(1).font = { bold: true };
        worksheet.autoFilter = "A1:H1";
        worksheet.views = [{ state: "frozen", ySplit: 1 }];

        filteredData.forEach((pay) => {
            const row = worksheet.addRow({
                client: pay.fullName,
                invoice: pay.invoice,
                invoiceDate: pay.invoiceDate ? new Date(pay.invoiceDate) : "",
                amount: pay.amount,
                status: pay.status,
                note: pay.note || "",
                event: pay.nameEvent + " - " + pay.yearEvent,
                contract: pay.numberContract.toString().padStart(3, "0") + " del " + format(new Date(pay.dateContract), 'dd/MM/yyyy', { locale: it }),
                amountContract: pay.amountContract,
                taxable: pay.taxable,
                iva: pay.iva ? "Si" : "No"
            });
            row.getCell("amount").numFmt = '€ #,##0.00';
            row.getCell("amountContract").numFmt = '€ #,##0.00';
            row.getCell("taxable").numFmt = '€ #,##0.00';
            row.getCell("invoiceDate").numFmt = "dd/mm/yyyy";
        });

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(
            new Blob([buffer], {
              type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            }),
            `notecredito_${getFormattedDate()}.xlsx`
        );
    }

    const handleActionClick = (index,e) => {
        setOpenAction(openAction === index ? null : index);
    };

    function getPaymentStatus(dueDate,payDate,mode) {
        if (!dueDate) return "";

        let diffDays = differenceInDays(new Date(), new Date(dueDate));
        if (payDate) {
            diffDays = differenceInDays(new Date(payDate), new Date(dueDate));
            if (diffDays > 15) return "danger";
            if (diffDays > 5 && "Bonifico" === mode) return "warning";
            else if (diffDays > 10) return "warning";
        }

        if (diffDays > 15 && !payDate) return "danger";
        if (diffDays > 5 && "Bonifico" === mode && !payDate) return "warning";
        else if (diffDays > 10) return "warning";
        return "";
    }
    function getStatus(status) {
        if (status === "Pagato") return "text-success";
        if (status === "In Attesa") return "text-warning";
        return "text-danger";
    }

  	return (
  	<>
  	    <div className="row">
            <div className="col-md-12">
                {/* Nav SchedulePay */}
                <Nav />
            </div>
        </div>
        <div className="row text-nowrap">
            <div className="col-md-12 mb-6">
                <div className="card card-action">
                    <div className="card-header align-items-center">
                        <h5 onClick={() => handleFilterClick(0)} className={`card-action-title mb-0 cursor-pointer ${openFilter === 0 ? "show" : ""}`} >
                            <i className="bx bx-slider me-2 bx-sm"></i>Filtri
                        </h5>
                        <div className="card-action-element">
                            <button type="button" className="btn btn-primary btn-sm edit-address me-3" onClick={onDefault}>Default</button>
                            <button type="button" className="btn btn-label-danger btn-sm edit-address" onClick={onReset}>Reset</button>
                        </div>
                    </div>
                    <div className={`offcanvas offcanvas-end ${openFilter === 0 ? "show" : ""}`} >
                        <div className="offcanvas-header border-bottom">
                            <h5 className="offcanvas-title">Filtri disponibili</h5>
                            <button onClick={() => handleFilterClick(0)} className="btn-close text-reset"></button>
                        </div>
                        <div className="offcanvas-body mx-0 flex-grow-0 p-6 h-100">
                            <form className="add-new-user pt-0" onSubmit={handleSubmit(fetchPaysFilter)}>
                                <div className="row">
                                    <div className="col mb-6">
                                        <label className="form-label">Anno</label>
                                            <Controller
                                            name="years"
                                            control={control}
                                                render={({ field }) => (
                                                    <Select
                                                        {...field}
                                                        placeholder="Seleziona"
                                                        isMulti
                                                        classNamePrefix='filter'
                                                        options={yearOptions}
                                                    />
                                                )}
                                            />
                                    </div>
                                    <div className="col mb-6">
                                        <label className="form-label">Mese</label>
                                            <Controller
                                            name="months"
                                            control={control}
                                                render={({ field }) => (
                                                    <Select
                                                        {...field}
                                                        placeholder="Seleziona"
                                                        isMulti
                                                        classNamePrefix='filter'
                                                        options={monthOptions}
                                                    />
                                                )}
                                            />
                                    </div>
                                </div>
                                <button type="submit" className="btn btn-primary me-3">Filtra</button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
  	    <div className="card">
            {/* List SchedulePay */}
            <div className="card-datatable table-responsive">
                <div className="dataTables_wrapper dt-bootstrap5 no-footer">
                    <div className="row">
                        <div className="col-md-2">
                            <div className="dataTables_length">
                                <label>
                                    <select name="DataTables_Table_0_length" className="form-select" id="itemsPerPage"
                                        value={itemsPerPageLocal} onChange={handleItemsPerPageChange}>
                                        {generateItemsPerPageOptions().map((value) => (
                                            <option key={value} value={value}>{value}</option>
                                        ))}
                                    </select>
                                </label>
                            </div>
                        </div>
                        <div className="col-md-10">
                            <div className="dt-action-buttons text-xl-end text-lg-start text-md-end text-start d-flex align-items-center justify-content-end flex-md-row flex-column mb-6 mb-md-0 mt-n6 mt-md-0">
                                <div className="dataTables_filter">
                                    <label>
                                        <input className="form-control" type="text"
                                            placeholder="Ricerca veloce ..."
                                            {...register('search')}
                                        />
                                    </label>
                                </div>
                                <div className="dt-buttons btn-group flex-wrap">
                                    <div className="btn-group">
                                        <button onClick={handleExportExcel} className="btn buttons-collection dropdown-toggle btn-label-secondary mx-4" type="button" >
                                            <span><i className="bx bx-export me-2 bx-sm"></i>Export</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <table className="datatables-users table border-top dataTable no-footer dtr-column">
                        <thead>
                            <tr>
                                <th></th>
                                <th>NOTE</th>
                                <th className="cursor-pointer" onClick={() => sortData("fullName")}>
                                    CLIENTE {sortConfig.key === "fullName" && (
                                            <span>{sortConfig.direction === "asc" ? "▲" : ""} {sortConfig.direction === "desc" ? "▼" : ""}</span>
                                    )}
                                </th>
                                <th className="cursor-pointer" onClick={() => sortData("invoice")}>
                                    FATTURA {sortConfig.key === "invoice" && (
                                            <span>{sortConfig.direction === "asc" ? "▲" : ""} {sortConfig.direction === "desc" ? "▼" : ""}</span>
                                    )}
                                </th>
                                <th>DATA FATTURA</th>
                                <th className="cursor-pointer" onClick={() => sortData("taxable")}>
                                    PAGAMENTO {sortConfig.key === "taxable" && (
                                            <span>{sortConfig.direction === "asc" ? "▲" : ""} {sortConfig.direction === "desc" ? "▼" : ""}</span>
                                    )}
                                </th>
                                <th>ESITO</th>
                                <th>AZIONI</th>
                            </tr>
                        </thead>
                        {loading && (
                            <tbody>
                                <tr className="odd">
                                    <td className="dataTables_empty">Loading...</td>
                                </tr>
                            </tbody>
                        )}
                        <tbody>
                            {currentItems.map((result,index) => (
                            <React.Fragment key={result.id_schedule_pay}>
                                <tr className={`${openAction === index  ? "change-icon" : ""} ${getPaymentStatus(result.dueDate,result.datePay,result.mode)} ${getStatus(result.status)}`}>
                                    <td onClick={(e) => handleActionClick(index,e)} className={`dt-control`}></td>
                                    <td data-tooltip-id={`tooltip-note-${result.id_schedule_pay}`}
                                        data-tooltip-content={`${result.note}`}>
                                        <i className={`bx bxs-circle bx-xs ${result.note !== "" ? "text-info" : "text-not-info"}`}></i>
                                    </td>
                                    <td>
                                        <a onClick={() => togglePaysLayer(result,"client")}>
                                            {result.fullName}
                                        </a>
                                    </td>
                                    <td>{result.invoice}</td>
                                    <td>{result.invoiceDate ? format(new Date(result.invoiceDate), 'dd/MM/yyyy', { locale: it }) : ''}</td>
                                    <td>{formatCurrency(result.amount)}</td>
                                    <td data-tooltip-id={`tooltip-date-${result.id_schedule_pay}`}
                                        data-tooltip-content={`${result.datePay ? format(new Date(result.datePay), 'dd MMMM yyyy', { locale: it }) : ''}`}>
                                        {result.status}
                                    </td>
                                    <td>
                                        <div className="d-flex align-items-center">
                                            <a className="p-2" onClick={() => toggleLayerEdit(result)}>
                                                <i className="bx bx-edit-alt me-1 text-not-info"></i>
                                            </a>
                                        </div>
                                    </td>
                                    <Tooltip id={`tooltip-note-${result.id_schedule_pay}`} place="right" className="tooltip-custom" />
                                    <Tooltip id={`tooltip-date-${result.id_schedule_pay}`} place="right" className="tooltip-custom" />
                                </tr>
                                {openAction === index && (
                                <tr className={`details`}>
                                    <td colSpan="12">
                                        <table className="datatables-users table border-top dataTable no-footer dtr-column">
                                            <thead>
                                                <tr>
                                                    <th onClick={() => sortData("nameEvent")}>
                                                        Evento {sortConfig.key === "nameEvent" && (
                                                                <span>{sortConfig.direction === "asc" ? "▲" : ""} {sortConfig.direction === "desc" ? "▼" : ""}</span>
                                                        )}
                                                    </th>
                                                    <th>Contratto</th>
                                                    <th>Imponibile Contratto</th>
                                                    <th>Imponibile Rata</th>
                                                    <th>Iva</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>
                                                        <a onClick={() => togglePaysLayer(result,"event")}>
                                                            {result.nameEvent} - {result.yearEvent}
                                                        </a>
                                                    </td>
                                                    <td>{result.numberContract.toString().padStart(3, "0")} del {result.dateContract ? format(new Date(result.dateContract), 'dd MMMM yyyy', { locale: it }) : ''}</td>
                                                    <td>{formatCurrency(result.amountContract)}</td>
                                                    <td>{formatCurrency(result.taxable)}</td>
                                                    <td>{result.iva ? "Si" : "No"}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </td>
                                </tr>
                                )}
                            </React.Fragment>
                            ))}
                         </tbody>
                    </table>
                    <div className="row">
                        <div className="col-sm-12 col-md-6">
                            <div className="dataTables_info" role="status" aria-live="polite">
                                Risultati ottenuti : {totalResults}
                            </div>
                        </div>
                        <div className="col-sm-12 col-md-6">
                            <div className="dataTables_paginate paging_simple_numbers custom-ryo">
                                {!loading && (
                                    <div className="pagination">
                                        <button className="page-link" onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}>
                                            <i className="bx bx-chevron-left bx-18px"></i>
                                        </button>
                                        {Array.from({ length: totalPages }, (_, index) => (
                                            <button
                                              key={index + 1}
                                              onClick={() => goToPage(index + 1)}
                                              disabled={currentPage === index + 1}
                                              className={`page-link ${currentPage === index + 1  ? "current-page" : ""}`}
                                            >
                                              {index + 1}
                                            </button>
                                        ))}
                                        <button className="page-link" onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}>
                                            <i className="bx bx-chevron-right bx-18px"></i>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* Slide */}
            { loadLayerEdit && pay && (
                <LayerEdit show={isLayerOpen} onClose={toggleLayerEdit} onFetchPay={onFetchPaysAfterEdit} schedulePay={pay}/>
                )
            }
            { loadPaysLayer && filter && (
                <LayerPays show={isPaysLayerOpen} onClose={toggleClosePaysLayer} schedulePays={filter} type={type}/>
                )
            }
            {/* Slide */}
            {/* /List SchedulePay*/}
  	    </div>
    </>
	);
}